import React, { useState, useEffect } from 'react';
import { Loader } from 'components/Base';
import Store from 'store/store';
import { compose } from 'ramda';
import api from 'api';
import {
  QuestionType as CapacityForLossQuestionType,
  ResponseType as CapacityForLossResponseType
} from 'store/modules/CapacityForLoss/types';
import withMeta from 'hoc/withMeta';
import QuestionaireForm from 'components/CustomerQuestionnaire/QuestionaireForm';
import { errHelper, Notify } from 'utils';
import { Button } from 'components/Base';
import { makeStyles } from '@material-ui/core';
import classnames from 'classnames';

interface CapacityForLossQuestionnaireProps {
  capacityForLossQuestions: Array<CapacityForLossQuestionType>;
  capacityForLossResponses: Array<CapacityForLossResponseType>;
  setCapacityForLossQuestions: any;
  setCapacityForLossResponses: any;
  customer: number;
  portfolio: number;
  onSuccess: Function;
  form?: string;
  customerName?: string;
  complete?: Function;
  showReviewButtons: boolean;
  getResponses: boolean;
  getQuestions?: Function | null;
  submitQuestions?: Function | null;
  showBlankReportButton?: boolean;
}

interface QuestionaireValues {
  [key: string]: string;
}

const { connect, selectors, actions } = Store;

const useStyles = makeStyles({
  blankFormButton: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem'
  }
});

const CapacityForLossQuestionnaire = ({
  capacityForLossQuestions,
  capacityForLossResponses,
  setCapacityForLossQuestions,
  setCapacityForLossResponses,
  customer,
  portfolio,
  onSuccess,
  customerName,
  form = 'questionaire',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  complete = () => {},
  showReviewButtons = true,
  getResponses = true,
  getQuestions = null,
  submitQuestions = null,
  showBlankReportButton = true
}: CapacityForLossQuestionnaireProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBlank, setIsLoadingBlank] = useState(false);
  const [isLoadingCompleted, setIsLoadingCompleted] = useState(false);
  const [surveyData, setSurveyData] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const tempSurveyData = [] as any;

    (
      (getQuestions && getQuestions()) ||
      api.getCapacityForLossQuestions()
    ).then(({ data }: { data: any }) => {
      setCapacityForLossQuestions(data);
      data.forEach((question: any) => {
        const joiner = ' If yes, ';
        const questionParts = question.name.split(joiner);
        tempSurveyData.push({
          id: question.value,
          text: `${questionParts[0]}`,
          responses: [
            { id: 1, text: 'Yes' },
            { id: 0, text: 'No' }
          ],
          label: questionParts[1]
            ? questionParts[1].charAt(0).toUpperCase() +
              questionParts[1].slice(1)
            : '',
          joiner
        });
      });
      setSurveyData(tempSurveyData);
      if (getResponses) {
        api.getCapacityForLossResponses({ portfolio }).then(({ data }: any) => {
          setCapacityForLossResponses(
            data.results.filter(
              (result: any) => Number(result.customer) === Number(customer)
            )
          );
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (values: QuestionaireValues) => {
    const data = [] as any;
    capacityForLossQuestions
      .map(question => question.value)
      .forEach(id => {
        const response = JSON.parse(values[`question_${id}`]);
        response.response = !!response.response_id;
        response.details = values[`question_${id}_details`] || null;
        response.case = portfolio;
        response.customer = customer;
        data.push(response);
      });

    (
      (submitQuestions && submitQuestions(data)) ||
      api.submitCapacityForLossResponses({
        portfolio,
        responses: { responses: data }
      })
    )
      .then(() => {
        onSuccess();
      })
      .catch((err: any) => {
        errHelper(err);
      });
  };

  const displayTextarea = (question_id: number, answer: string) => {
    return answer === 'Yes';
  };

  const userResponses = capacityForLossResponses.map(response => {
    return {
      response_id: +response.response,
      ...response
    };
  });

  const classes = useStyles();

  const downloadQuestionnare = (blank = true) => {
    let method;

    if (blank) {
      setIsLoadingBlank(true);
      method = api.blankQuestionnaire('cfl');
    } else {
      if (!userResponses.length) {
        return Notify.info('Info', 'Please fill out questionnaire');
      }
      setIsLoadingCompleted(true);
      method = api.downloadQuestionnare({
        questionnaireId: null,
        questionnaire2Id: null,
        images: undefined,
        docType: 'CFL',
        customerId: customer,
        caseId: portfolio
      });
    }

    return method
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Capacity For Loss Questionnaire.pdf`);
        document.body.appendChild(link);
        link.click();
        setIsLoadingBlank(false);
        setIsLoadingCompleted(false);
      })
      .catch((err: any) => {
        setIsLoadingBlank(false);
        setIsLoadingCompleted(false);
        errHelper(err);
      });
  };

  return (
    <Loader isLoading={isLoading}>
      {showBlankReportButton && (
        <div className={classnames(classes.blankFormButton)}>
          <Button
            isLoading={isLoadingBlank}
            onClick={() => downloadQuestionnare()}
          >
            Print Blank Form
          </Button>
          <Button
            isLoading={isLoadingCompleted}
            onClick={() => downloadQuestionnare(false)}
          >
            Print Completed Form
          </Button>
        </div>
      )}
      <QuestionaireForm
        form={form}
        onSubmit={submit}
        surveyData={surveyData}
        submitted={false}
        isSubmitting={false}
        successful={false}
        title="Capacity for Loss Questionnaire"
        displayTextarea={displayTextarea}
        userResponses={userResponses}
        customerId={customer}
        customerName={customerName}
        complete={complete}
        showReviewButtons={showReviewButtons}
      >
        <h4>
          Capacity for loss is the amount of money that you could afford to lose
          without having a significant negative impact on your financial
          situation or standard of living. It is an important concept to
          consider when making investment decisions, as it can help to ensure
          that you are not taking on too much risk.
        </h4>
        <p>
          The following questions are designed to help us understand your
          capacity for loss
        </p>
      </QuestionaireForm>
    </Loader>
  );
};

export default compose(
  connect(
    (state: any) => ({
      capacityForLossQuestions:
        selectors.capacityForLossSelectors.getQuestions(state),
      capacityForLossResponses:
        selectors.capacityForLossSelectors.getResponses(state)
    }),
    {
      setCapacityForLossQuestions: actions.capacityForLossActions.setQuestions,
      setCapacityForLossResponses: actions.capacityForLossActions.setResponses
    }
  ),
  withMeta
)(CapacityForLossQuestionnaire);
